import { takeLatest, takeEvery, call, put, debounce, select, takeLeading, all } from 'redux-saga/effects';
import { when, is, prop, pick } from 'ramda';

import { withAlert } from 'store/alerts';
import { STATUS, EXAMINATION_STATUS, FILE_ID, FILE_URL, FILE_URL_EXPIRES, BUCKET, MIMETYPE } from 'store/examinations';
import { saveExamination } from 'store/examinations/actions';
import { fetchInvoicesByExamId } from 'store/invoices/actions';
import { getUserProp } from 'store/session/selectors';
import api from 'api';

import { ID, SUB_EXAMINATION, APPROVALS, SUB_USER, SUB_PHYSICIAN } from '.';
import { getCurrentDiagnosisProp } from './selectors';
import { updateDiagnosisCodes, updateDiagnosisReports, updateDiagnoses, updateDiagnosis } from './actions';
import {
  FETCH_DIAGNOSIS_CODES,
  FETCH_DIAGNOSES,
  FETCH_DIAGNOSES_FOR_EXAMINATION,
  FETCH_DIAGNOSIS_REPORTS,
  SAVE_DIAGNOSIS,
  CREATE_DIAGNOSIS_PREVIEW_REPORT,
  WITHDRAW_DIAGNOSIS,
  TAKE_OVER_DIAGNOSIS,
  LOAD_ANALYSIS_IMAGES,
} from './types';

function* fetchDiagnosisCodes() {
  const { data } = yield call(api.getDiagnosisCodes);
  yield put(updateDiagnosisCodes(data));
}

function* fetchDiagnosisReports() {
  const diagnosisId = yield select(getCurrentDiagnosisProp(ID));
  const { data } = yield call(api.getDiagnosisReports, diagnosisId);

  yield put(updateDiagnosisReports(data));
}

function* fetchDiagnoses({ payload }) {
  const diagnoses = yield call(api.getExaminationDiagnosis, payload);

  yield put(updateDiagnoses(diagnoses));
  yield call(fetchDiagnosisReports);
}

function* fetchDiagnosesForExamination({ payload }) {
  return { success: yield call(api.getExaminationDiagnosis, payload) };
}

function* saveDiagnosis({ payload }) {
  const data = yield call(
    api.patchDiagnosis,
    payload[APPROVALS]
      ? {
          ...payload,
          [APPROVALS]: payload[APPROVALS].map((approval) => ({
            ...approval,
            [SUB_USER]: when(is(Object), prop(ID))(approval[SUB_USER]),
          })),
        }
      : payload
  );
  yield put(updateDiagnosis(data));
}

function* takeOverDiagnosis() {
  const userId = yield select(getUserProp(ID));
  const diagnosisId = yield select(getCurrentDiagnosisProp(ID));

  yield saveDiagnosis({ payload: { diagnosisId, [SUB_PHYSICIAN]: userId } });
}

function* createDiagnosisPreviewReport({ payload }) {
  yield call(api.getDiagnosisReportPreview, payload);
}

function* withdrawDiagnosis({ payload }) {
  const diagnosis = yield call(api.postDiagnosisWithdraw, payload);
  const { data } = yield call(api.getDiagnosisReports, payload);

  yield put(saveExamination({ [ID]: diagnosis[SUB_EXAMINATION], [STATUS]: EXAMINATION_STATUS.UNDER_REVIEW }));
  yield put(updateDiagnosis(diagnosis));
  yield put(updateDiagnosisReports(data));
  yield put(fetchInvoicesByExamId(diagnosis[SUB_EXAMINATION]));
}

function* loadAnalysisImages({ payload }) {
  const data = yield all(
    payload.map(async (item) => ({
      ...item,
      files: await Promise.all(
        (item.files || []).map(async (imgData) => ({
          ...imgData,
          url: await api.loadFile(pick([FILE_ID, FILE_URL, FILE_URL_EXPIRES, BUCKET, MIMETYPE], imgData)),
        }))
      ),
    }))
  );

  return { success: data };
}

export default function* watchDiagnoses() {
  yield takeLatest(FETCH_DIAGNOSIS_CODES, withAlert(fetchDiagnosisCodes));
  yield debounce(500, SAVE_DIAGNOSIS, withAlert(saveDiagnosis));
  yield takeLatest(FETCH_DIAGNOSES, withAlert(fetchDiagnoses));
  yield takeLatest(FETCH_DIAGNOSES_FOR_EXAMINATION, withAlert(fetchDiagnosesForExamination));
  yield takeLeading(FETCH_DIAGNOSIS_REPORTS, withAlert(fetchDiagnosisReports));
  yield takeLatest(CREATE_DIAGNOSIS_PREVIEW_REPORT, withAlert(createDiagnosisPreviewReport));
  yield takeEvery(WITHDRAW_DIAGNOSIS, withAlert(withdrawDiagnosis));
  yield takeLeading(TAKE_OVER_DIAGNOSIS, withAlert(takeOverDiagnosis));
  yield takeLeading(LOAD_ANALYSIS_IMAGES, withAlert(loadAnalysisImages));
}
